import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { SolSortDirection } from '@sol/components';
import { AbstractAsmService } from '@asm/components-core/services/abstract-asm.service';
import {
  PLATFORM_BANNER_PAGE_SIZE_CAP,
  PlatformBanner,
  PlatformBannerBoundary,
  PlatformBannerDetail,
  PlatformBannerDisplayItem,
  PlatformBannerInterval,
  PlatformBannerLevel,
  PlatformBannerStatus,
  PlatformBannerWritePayload,
} from '../models/platform-banner';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface PlatformBannerServiceState {}

// ---------------------------------------------------------------------------
// Backend DTOs (snake_case)
// ---------------------------------------------------------------------------

export interface PlatformBannerIntervalDto {
  readonly from: string;
  readonly to: string | null;
}

export interface PlatformBannerDto {
  readonly id: number;
  readonly level: PlatformBannerLevel;
  readonly message: string;
  readonly link_text: string | null;
  readonly link_url: string | null;
  readonly enabled: boolean;
  readonly status: PlatformBannerStatus;
  readonly starts_at: string | null;
  readonly ends_at: string | null;
  readonly last_active_from: string | null;
  readonly last_active_to: string | null;
  readonly active_period_count: number;
  readonly active_periods: readonly PlatformBannerIntervalDto[];
  readonly created_at: string;
  readonly created_by: string | null;
}

export interface PlatformBannerDetailDto extends PlatformBannerDto {
  readonly updated_at: string;
  readonly updated_by: string | null;
}

export interface PlatformBannerPage {
  readonly total: number;
  readonly number: number;
  readonly size: number;
}

export interface PlatformBannerListResponseDto {
  readonly items: readonly PlatformBannerDto[];
  readonly page: PlatformBannerPage;
}

export interface PlatformBannerDisplayItemDto {
  readonly id: number;
  readonly level: PlatformBannerLevel;
  readonly message: string;
  readonly link_text: string | null;
  readonly link_url: string | null;
}

export interface PlatformBannerDisplayResponseDto {
  readonly items: readonly PlatformBannerDisplayItemDto[];
}

export interface PlatformBannerWriteDto {
  readonly level: PlatformBannerLevel;
  readonly message: string;
  readonly link_text: string | null;
  readonly link_url: string | null;
  readonly enabled: boolean;
  readonly starts_at: string | null;
  readonly ends_at: string | null;
}

interface PlatformBannerActionDto {
  readonly enabled: boolean;
  readonly ends_at?: string;
}

export interface PlatformBannerListResult {
  readonly items: readonly PlatformBanner[];
  readonly page: PlatformBannerPage;
}

export interface FetchPlatformBannersOptions {
  readonly sortField?: string;
  readonly sortDirection?: SolSortDirection;
  /**
   * One OQL expression over the fields the `banners` data source publishes, or nothing when
   * unfiltered. The finished switch is one of those fields, so it rides inside this expression.
   */
  readonly filter?: string;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class PlatformBannerService extends AbstractAsmService<PlatformBannerServiceState> {
  constructor() {
    super('/api/v3/banners', {});
  }

  public fetchBanners(options: FetchPlatformBannersOptions): Observable<PlatformBannerListResult> {
    return this.httpGet<PlatformBannerListResponseDto>('', {
      params: {
        page_size: PLATFORM_BANNER_PAGE_SIZE_CAP,
        ordering: AbstractAsmService.toOrdering(options.sortField, options.sortDirection),
        filter: options.filter || undefined,
      },
    }).pipe(
      map(response => ({
        items: response.items.map(dto => PlatformBannerService.fromDto(dto)),
        page: response.page,
      })),
      this.asAsmError('GET /api/v3/banners'),
    );
  }

  public fetchBanner(id: number): Observable<PlatformBannerDetail> {
    return this.httpGet<PlatformBannerDetailDto>(`/${id}`).pipe(
      map(dto => PlatformBannerService.fromDetailDto(dto)),
      this.asAsmError(`GET /api/v3/banners/${id}`),
    );
  }

  public createBanner(payload: PlatformBannerWritePayload): Observable<PlatformBanner> {
    return this.httpPost<PlatformBannerWriteDto, PlatformBannerDto>('', PlatformBannerService.toWriteDto(payload)).pipe(
      map(dto => PlatformBannerService.fromDto(dto)),
      this.asAsmError('POST /api/v3/banners'),
    );
  }

  public updateBanner(id: number, payload: PlatformBannerWritePayload): Observable<PlatformBanner> {
    return this.httpPatch<PlatformBannerWriteDto, PlatformBannerDto>(
      `/${id}`,
      PlatformBannerService.toWriteDto(payload),
    ).pipe(
      map(dto => PlatformBannerService.fromDto(dto)),
      this.asAsmError(`PATCH /api/v3/banners/${id}`),
    );
  }

  /** Enable: run the banner again on whatever schedule it holds. */
  public enableBanner(id: number): Observable<PlatformBanner> {
    return this.patchAction(id, { enabled: true });
  }

  /** Stop the banner and record the instant it stopped; the server clears a passed end left enabled. */
  public disableBanner(id: number, endsAt: PlatformBannerBoundary): Observable<PlatformBanner> {
    return this.patchAction(id, { enabled: false, ends_at: endsAt });
  }

  public deleteBanner(id: number): Observable<void> {
    return this.httpDelete<void>(`/${id}`);
  }

  public fetchDisplayBanners(scope: string): Observable<PlatformBannerDisplayItem[]> {
    return this.httpGet<PlatformBannerDisplayResponseDto>('/live', { params: { scope } }).pipe(
      map(response => response.items.map(dto => PlatformBannerService.fromDisplayDto(dto))),
      this.asAsmError('GET /api/v3/banners/live'),
    );
  }

  private patchAction(id: number, body: PlatformBannerActionDto): Observable<PlatformBanner> {
    return this.httpPatch<PlatformBannerActionDto, PlatformBannerDto>(`/${id}`, body).pipe(
      map(dto => PlatformBannerService.fromDto(dto)),
      this.asAsmError(`PATCH /api/v3/banners/${id}`),
    );
  }

  // ---------------------------------------------------------------------------
  // DTO -> domain
  // ---------------------------------------------------------------------------

  public static fromDto(dto: PlatformBannerDto): PlatformBanner {
    return {
      id: dto.id,
      level: dto.level,
      message: dto.message,
      linkText: dto.link_text ?? undefined,
      linkUrl: dto.link_url ?? undefined,
      enabled: dto.enabled,
      status: dto.status,
      startsAt: dto.starts_at ?? undefined,
      endsAt: dto.ends_at ?? undefined,
      lastActiveFrom: dto.last_active_from ?? undefined,
      lastActiveTo: dto.last_active_to ?? undefined,
      activePeriodCount: dto.active_period_count,
      activePeriods: dto.active_periods.map(period => PlatformBannerService.fromIntervalDto(period)),
      createdAt: new Date(dto.created_at),
      createdBy: dto.created_by ?? undefined,
    };
  }

  public static fromDetailDto(dto: PlatformBannerDetailDto): PlatformBannerDetail {
    return {
      ...PlatformBannerService.fromDto(dto),
      updatedAt: new Date(dto.updated_at),
      updatedBy: dto.updated_by ?? undefined,
    };
  }

  public static fromDisplayDto(dto: PlatformBannerDisplayItemDto): PlatformBannerDisplayItem {
    return {
      id: dto.id,
      level: dto.level,
      message: dto.message,
      linkText: dto.link_text ?? undefined,
      linkUrl: dto.link_url ?? undefined,
    };
  }

  private static fromIntervalDto(dto: PlatformBannerIntervalDto): PlatformBannerInterval {
    return {
      from: dto.from,
      to: dto.to ?? undefined,
    };
  }

  // ---------------------------------------------------------------------------
  // Domain -> DTO
  // ---------------------------------------------------------------------------

  public static toWriteDto(payload: PlatformBannerWritePayload): PlatformBannerWriteDto {
    return {
      level: payload.level,
      message: payload.message,
      link_text: payload.linkText ?? null,
      link_url: payload.linkUrl ?? null,
      enabled: payload.enabled,
      starts_at: payload.startsAt ?? null,
      ends_at: payload.endsAt ?? null,
    };
  }
}
